/**
 * I campi di una scheda anagrafica che hanno una forma: la posta e il telefono.
 *
 * ## Il difetto, che è F07 del registro
 *
 * «Anagrafiche utente con duplicati e campi liberi», e la bonifica scritta accanto:
 * «codice fiscale 16 caratteri maiuscolo, email normalizzata, telefono in formato
 * E.164». Dei tre, il codice fiscale era già a posto — sta in `api.ts`, accanto ai
 * sette pezzi da cui la sua forma è costruita, e i tre DTO che una persona la
 * scrivono lo passano già da `.trim().toUpperCase()` e dalla forma. Gli altri due
 * no, e il perché è più interessante del che cosa.
 *
 * ## La posta, che era normalizzata solo nella rubrica
 *
 * `CreateContactDto` la mette in minuscolo prima di guardarla, e accanto c'è
 * scritto il motivo: «un indirizzo si confronta, non si legge ad alta voce». Le tre
 * classi che scrivono la scheda di un **dipendente** — quella che la crea, quella
 * che la corregge, quella del primo amministratore — la tosavano soltanto. Lo stesso
 * dato con due politiche, che è la condizione in cui una delle due invecchia da sola.
 *
 * Nel database di produzione la differenza oggi non si vede: 490 indirizzi
 * personali, **nessuno** con una maiuscola, nessuno con uno spazio ai bordi. Non è
 * un argomento per lasciar perdere — è la ragione per cui nessuno se ne accorgerà il
 * giorno in cui `Mario.Rossi@…` e `mario.rossi@…` diventeranno due righe.
 *
 * ## Il telefono, e una regola che si reggeva su un fatto mai misurato
 *
 * Qui accanto, in `api.ts`, `TELEFONO_MAX` valeva quaranta e portava scritta la sua
 * giustificazione: «la colonna del vecchio conteneva anche "int. 214" e "chiamare
 * dopo le 18", e quaranta caratteri sono la misura di quel genere di cosa, non di un
 * numero». Quel numero non veniva da una misura: veniva da un'idea di che cosa una
 * colonna di testo *possa* contenere.
 *
 * Contati sulle righe vere — le tre colonne di telefono delle persone, quella delle
 * sedi, quella dei luoghi, quella della rubrica — i valori sono **1296**. Quelli che
 * contengono anche una sola lettera sono **zero**. Tolti spazi, punti, trattini,
 * parentesi e barre, sono tutti e 1296 cifre e nient'altro; uno solo è più corto di
 * sette cifre, ed è la stringa `"0"`. La colonna libera non conteneva niente di
 * libero: conteneva numeri scritti in quattro modi diversi.
 *
 * ## Perché non E.164, che è quello che il registro chiedeva
 *
 * Perché E.164 vieta lo zero iniziale — è la sua definizione: prefisso internazionale
 * e poi il numero nazionale *senza* lo zero — e i numeri fissi italiani scritti in
 * forma nazionale cominciano tutti per zero. La forma `^\+?[1-9]\d{6,17}$`, che il
 * sistema aveva già e usava per la sola rubrica, applicata alle righe vere rifiuta
 * **159 telefoni d'ufficio su 159**, tutti e 6 quelli delle sedi e 131 dei 153 dei
 * luoghi: 296 numeri buoni buttati fuori per far posto a una regola giusta altrove.
 * E nel frattempo accetta senza fiatare un numero da sette cifre che nessuno può
 * comporre. Era severa dove non serviva e larga dove contava.
 *
 * Quello che serve davvero sono due cose diverse, e vanno tenute diverse:
 *
 * 1. **che lo stesso numero non si conservi in due modi.** Se ne occupa
 *    `ripulisciTelefono`, che toglie la punteggiatura che le persone scrivono. È la
 *    stessa ragione della rubrica: `+39 055 123` e `+39055123` sono un telefono solo,
 *    e conservarli tutti e due è il modo in cui un'anagrafica si sdoppia.
 * 2. **che quello che resta sia un numero.** Se ne occupa `TELEFONO_COMPONIBILE`, che
 *    è la forma di prima con il divieto dello zero tolto e nient'altro cambiato: le
 *    stesse da sette a diciotto cifre, lo stesso `+` facoltativo. Sulle 1296 righe
 *    vere ne rifiuta **una**, ed è quella che vale `"0"`.
 *
 * Il tetto non è più un numero deciso: è la conseguenza della forma — diciotto cifre
 * più il segno. Quaranta era irraggiungibile, e un tetto irraggiungibile è un tetto
 * che nessuno controllerà mai insieme alla regola che lo rende tale (§60).
 *
 * ## Il `tel:` che quattro schermate si costruivano da sole
 *
 * `DipendenteView`, `FascicoloCessatoView`, `SchermataSedi` e `SchermataRubrica`
 * componevano l'indirizzo da comporre con `.replace(/\s/g, '')` — soltanto gli
 * spazi. Sui numeri veri esistono un `0577-1234567` e un `0577/1234567`: il primo
 * finiva in un `tel:` con un trattino dentro, il secondo con una **barra**, che in un
 * URI non è un carattere qualunque. Quattro copie della stessa riga, tutte e quattro
 * incomplete allo stesso modo: `numeroComponibile` è quella riga scritta una volta,
 * e restituisce `null` quando quello che c'è nella colonna non è un numero — perché
 * una vecchia riga che il confine non ha mai attraversato può contenere qualunque
 * cosa, e offrire di chiamare «chiamare dopo le 18» è peggio che non offrirlo.
 */
/**
 * Da sette a diciotto cifre: la stessa ampiezza che aveva `^\+?[1-9]\d{6,17}$` —
 * una cifra più altre da sei a diciassette — riscritta in modo che si legga.
 * Cambia solo che la prima cifra può essere uno zero.
 */
export const TELEFONO_CIFRE = { min: 7, max: 18 };
/**
 * Un numero che si può comporre, scritto come lo si compone.
 *
 * Il `+` è facoltativo perché in questa azienda i numeri nazionali sono la
 * maggioranza — 296 dei 1296 cominciano per zero, e sono i fissi — e pretendere il
 * prefisso internazionale vorrebbe dire aggiungerlo noi a un numero che non lo porta.
 * Dedurre `+39` da uno zero iniziale è indovinare una nazione: giusto quasi sempre e
 * scritto da nessuna parte, cioè il genere di cosa che si scopre quando è sbagliata.
 */
export const TELEFONO_COMPONIBILE = new RegExp(`^\\+?\\d{${TELEFONO_CIFRE.min},${TELEFONO_CIFRE.max}}$`);
/** Diciotto cifre e il segno davanti: il tetto è quello che la forma consente, non un numero a parte. */
export const TELEFONO_MAX = TELEFONO_CIFRE.max + 1;
/**
 * Quanto si può **battere** in una casella, che non è quanto si conserva.
 *
 * Il tetto di sopra vale sul numero pulito, perché è quello che finisce nella colonna:
 * il `@MaxLength` del server guarda il valore dopo `@Transform`, cioè dopo che la
 * punteggiatura è stata tolta. Un campo di testo no — lì la punteggiatura la sta ancora
 * battendo una persona, e un `maxlength` pari al tetto conservato le taglierebbe il
 * numero sotto le dita mentre lo scrive spaziato.
 *
 * Diciannove più un separatore ogni due cifre. Il più lungo fra i 1143 numeri veri ne
 * misura **dodici**: questo tetto non lo tocca nessuno, ed è quello che deve fare —
 * fermare chi incolla mezza pagina, non chi scrive un prefisso internazionale.
 */
export const TELEFONO_MAX_SCRITTO = TELEFONO_MAX + Math.floor(TELEFONO_CIFRE.max / 2);
/**
 * La punteggiatura che le persone scrivono dentro un numero, tolta.
 *
 * `\s` in JavaScript comprende anche lo spazio unificatore (`U+00A0`), che è quello
 * che arriva quando un numero viene incollato da una pagina web e che a schermo non
 * si distingue da uno spazio normale. Scritto per nome e non battuto: un carattere
 * invisibile dentro un commento è una trappola per chi legge dopo.
 */
export function ripulisciTelefono(testo) {
    return testo.replace(/[\s.\-()/]/g, '');
}
/**
 * L'indirizzo di posta come si conserva: tosato e in minuscolo.
 *
 * Il nome della casella è, per lo standard, sensibile alle maiuscole; nessun
 * fornitore di posta di questo mondo si comporta però così, e trattarle come diverse
 * significa avere due righe per una persona sola. Fra i due errori possibili si
 * sceglie quello che non sdoppia le persone.
 */
export function normalizzaPosta(testo) {
    return testo.trim().toLowerCase();
}
/**
 * Che cosa mettere dopo `tel:`, oppure `null` se quello che c'è non è un numero.
 *
 * Le colonne vecchie non sono passate da nessun confine, e quelle nuove ci passano
 * solo da quando questo file esiste: chi disegna una schermata non può dare per
 * scontato che dentro `phoneNumber` ci sia un telefono. Un tasto «chiama» che non
 * chiama è peggio di un tasto che non c'è, perché la persona lo preme e aspetta.
 */
export function numeroComponibile(testo) {
    if (testo === null || testo === undefined)
        return null;
    const pulito = ripulisciTelefono(testo);
    return TELEFONO_COMPONIBILE.test(pulito) ? pulito : null;
}
//# sourceMappingURL=anagrafica.js.map